<template>
	<main>
		<van-form
			ref="formRef"
			validate-first
			:show-error-message="false"
			@submit="onSubmit"
		>
			<van-cell-group>
				<van-notice-bar
					v-if="!isChannel && maintenanceActive"
					left-icon="info-o"
				>
					{{ $t('系统维护升级中，请稍后再试') }}
				</van-notice-bar>
				<van-field
					v-model="form.authCode"
					:disabled="!isChannel && maintenanceActive"
					:placeholder="authCodePlaceholder"
					:rules="[{ required: true, message: authCodePlaceholder }]"
				>
					<template #button v-if="isChannel || !maintenanceActive">
						<van-icon size="30" name="scan" @click="onScan" />
					</template>
				</van-field>
				<van-field
					v-model="form.validateCode"
					:disabled="!isChannel && maintenanceActive"
					:placeholder="$t('请输入验证码')"
					:rules="[{ required: true, message: $t('请输入验证码') }]"
				>
					<template #button v-if="isChannel || !maintenanceActive">
						<van-image width="80" height="35" :src="codeUrl" @click="getCode" />
					</template>
				</van-field>
			</van-cell-group>
			<div style="margin: 16px 0">
				<van-button
					round
					block
					type="info"
					native-type="submit"
					:disabled="!isChannel && maintenanceActive"
					:class="{ 'btn-disabled': !isChannel && maintenanceActive }"
				>
					{{ submitText }}
				</van-button>
			</div>
		</van-form>

		<qrcode
			:show="qrcodeShow"
			ref="qrcodeRef"
			@scanSuccess="scanSuccess"
			@scanFail="qrcodeShow = false"
		/>

		<template v-if="isSubmit">
			<van-notice-bar
				:color="notice.color"
				:background="notice.background"
				:left-icon="notice.leftIcon"
			>
				{{ notice.tip }}
			</van-notice-bar>

			<van-form readonly v-if="isSuccess">
				<van-field
					v-for="(it, idx) in visibleList"
					:key="idx"
					v-model="formData[it.key]"
					:label="it.value"
				/>

				<template v-if="isChannel">
					<van-field :label="$t('授权书')">
						<template #input>
							<van-image
								width="80"
								height="60"
								:src="formData.content"
								@click="previewImageShow = true"
							/>
						</template>
					</van-field>

					<van-image-preview
						v-model="previewImageShow"
						:images="[formData.content]"
					/>
				</template>
			</van-form>

			<van-empty :image-size="100" v-if="isError" :description="errorContent" />
		</template>
	</main>
</template>

<script>
import { getCaptchaImage, getWmsUpdateTime } from '@/api/certificate';
import 'vant/lib/form/style/index.js';
import { chanelVerify, antiCounterfeitingCodeVerify } from '@/api/certificate';
import { JsApi, isWX } from '@/utils/wx';

const notice = {
	success: {
		color: '#07c160',
		background: '#e8f6f0',
		leftIcon: 'passed'
	},
	error: {
		color: '#ed3f14',
		background: '#fff2e8',
		leftIcon: 'warning-o'
	}
};

const xssFilters = {
	escapeHtml(text) {
		const map = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#039;'
		};
		return text.replace(/[&<>"']/g, m => map[m]);
	}
};

export default {
	components: {
		// 注册
		qrcode: () => import('@/components/QrcodeReader')
	},
	data() {
		return {
			codeUrl: '',
			form: {
				authCode: '',
				validateCode: ''
			},
			isSubmit: false,
			isSuccess: false,
			isError: false,
			formData: {
				name: '',
				date: '',
				content: '',
				count: ''
			},
			list: [],
			notifyShow: true,
			previewImageShow: false,
			qrcodeShow: false,
			notice: { tip: '' },
			errorContent: '',
			authCodePlaceholder: '',
			maintenanceActive: false
		};
	},
	computed: {
		isChannel() {
			return this.type === 'channel';
		},
		visibleList() {
			return this.list.filter(it => this.formData[it.key]);
		}
	},
	watch: {
		isChannel: {
			handler: function (val) {
				this.list = Object.freeze(
					val
						? [
								{ key: 'name', value: this.$t('授权客户名称') },
								{ key: 'date', value: this.$t('授权失效日期') }
						  ]
						: [
								{ key: 'name', value: this.$t('防伪码') },
								{ key: 'date', value: this.$t('查询时间') },
								{ key: 'content', value: this.$t('规格型号') },
								{ key: 'count', value: this.$t('查询次数') }
						  ]
				);

				this.authCodePlaceholder = val
					? this.$t('请输入授权码')
					: this.$t('请输入防伪码');

				this.submitText = val ? this.$t('立即验证') : this.$t('立即查询');
			},
			immediate: true
		}
	},
	props: {
		type: {
			type: String,
			validator: function (value) {
				return ['channel', 'antiCounterfeiting'].includes(value);
			},
			default: 'channel'
		}
	},
	created() {
		this.getCode();
		// 仅在规格防伪查询时执行维护时间判断
		if (!this.isChannel) {
			this.checkMaintenance();
		}
	},
	methods: {
		async checkMaintenance() {
			try {
				const res = await getWmsUpdateTime();
				console.log('res', res);
				if (res && res.code === 0) {
					const { startTime, endTime } = res.data;
					if (startTime && endTime) {
						const now = new Date();
						const start = new Date(startTime);
						const end = new Date(endTime);
						if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
							this.maintenanceActive = now >= start && now <= end;
						}
					}
				} else {
					this.maintenanceActive = false;
				}
			} catch (e) {
				console.error('获取维护时间失败', e);
				this.maintenanceActive = false;
			}
		},
		async onSubmit() {
			try {
				this.isSuccess = false;
				this.isError = false;
				// 在提交前过滤用户输入
				this.form.authCode = xssFilters.escapeHtml(this.form.authCode);
				this.form.validateCode = xssFilters.escapeHtml(this.form.validateCode);

				const api = this.isChannel
					? chanelVerify
					: antiCounterfeitingCodeVerify;

				const res = await api(this.form);

				if (res.code === 0) {
					this.isSubmit = true;

					this.isSuccess = true;
					this.notice = notice.success;

					this.notice.tip = this.isChannel
						? this.$t('验证成功')
						: this.$t('验证成功，溯源码存在');

					const data = JSON.parse(res.msg);

					this.formData.content = this.isChannel
						? data.img
						: data.ItemSpecificationModel;
					// 在显示数据时使用过滤后的值
					this.formData.name = this.isChannel
						? xssFilters.escapeHtml(data.customerName)
						: data.AntiCounterfeitingCode;

					this.formData.date = this.isChannel
						? data.expiryDate
						: data.QueryTime;

					if (!this.isChannel) {
						this.formData.count = data.QueryNum;
					}
				}
				// 数据不存在
				if (res.code === -1) {
					this.isSubmit = true;

					this.isError = true;
					this.notice = notice.error;

					this.notice.tip = this.isChannel
						? this.$t('未查询到记录')
						: this.$t('防伪码无效');
					this.errorContent = this.isChannel
						? this.$t(
								'抱歉，未能找到与该授权编码相关的记录。请检查授权编码是否正确，或联系我们的客服人员获取帮助。'
						  )
						: this.$t(
								'您输入的防伪码不存在或已失效。请仔细核对防伪码，如有疑问请联系我们的客服人员。'
						  );
				}
				if (res.code === 500) {
					this.isSubmit = false;
					this.$toast.fail(this.$t('验证码错误'));
				}
			} catch (e) {
				console.error('提交失败', e);
				this.$toast.fail(this.$t('提交失败，请稍后重试'));
				this.isSubmit = false;
			} finally {
				this.getCode();
			}
		},
		async getCode() {
			try {
				const res = await getCaptchaImage();

				let blob = new Blob([res]); // 使用响应头和二进制数据创建blob
				let url = window.URL.createObjectURL(blob);
				this.codeUrl = url;
				this.form.validateCode = '';
				blob = null;
				url = null;
			} catch (e) {
				console.error('获取验证码失败', e);
			}
		},
		async onScan() {
			// 扫码
			if (isWX()) {
				const res = await JsApi.scanQRCode();
				// 扫码成功之后，调用scanSuccess方法
				console.log('扫码 res', res);

				if (res) {
					this.scanSuccess(res);
				}
			} else {
				this.qrcodeShow = true;
				this.$refs.qrcodeRef.getCamerasInfo();
			}
		},
		scanSuccess(res) {
			this.form.authCode = res;
			this.qrcodeShow = false;
		}
	}
};
</script>

<style lang="scss" scoped>
.van-image {
	display: block;
}

.maintenance-tip {
	margin-bottom: 8px;
	padding: 10px 12px;
	background: #ffe9d5;
	color: #333333;
	font-size: 14px;
	border-radius: 4px;
}

.btn-disabled {
	background: #c0c4cc !important;
	border-color: #c0c4cc !important;
}
</style>
